import { Eye, ShieldAlert, TriangleAlert, type LucideIcon } from 'lucide-react';

import type { DangerTier } from '@/lib/permissions';

export interface DangerTierConfig {
  tier: DangerTier;
  label: string;
  /** Short phrase shown next to a group of actions, e.g. "bypasses the lock". */
  groupHint: string;
  icon: LucideIcon;
  /** Accent colour. Tier 3 is the only tier permitted to use red. */
  accent: string;
  accentSoft: string;
  accentRgb: string;
  surface: string;
  border: string;
  /** Foreground for a solid/filled button of this tier. */
  onAccent: string;
  /** Whether the action requires a typed reason recorded on the audit row. */
  requiresReason: boolean;
  /** Whether the operator must type the entity name to proceed. */
  requiresTypeToConfirm: boolean;
  /** Whether a confirmation step is shown at all. */
  requiresConfirmation: boolean;
}

/**
 * The three tiers, defined once.
 *
 * Every surface in the console derives its colour, icon and confirmation
 * choreography from this table, so the language is learned once and never varies.
 * Changing a tier's behaviour here changes it everywhere — which is the point.
 */
export const DANGER_TIER_CONFIG: Record<DangerTier, DangerTierConfig> = {
  1: {
    tier: 1,
    label: 'Safe',
    groupHint: 'reads, filters & exports',
    icon: Eye,
    accent: 'var(--tx2)',
    accentSoft: 'var(--tx3)',
    accentRgb: 'var(--tx2-rgb)',
    surface: 'var(--elev)',
    border: 'var(--bd)',
    onAccent: 'var(--tx)',
    requiresReason: false,
    requiresTypeToConfirm: false,
    requiresConfirmation: false,
  },
  2: {
    tier: 2,
    label: 'Write',
    groupHint: 'reversible',
    icon: TriangleAlert,
    accent: 'var(--warn)',
    accentSoft: 'var(--warn)',
    accentRgb: 'var(--warn-rgb)',
    surface: 'rgba(var(--warn-rgb), 0.1)',
    border: 'rgba(var(--warn-rgb), 0.38)',
    onAccent: 'var(--on-gold)',
    requiresReason: false,
    requiresTypeToConfirm: false,
    requiresConfirmation: true,
  },
  3: {
    tier: 3,
    label: 'Override',
    groupHint: 'bypasses app rules',
    icon: ShieldAlert,
    accent: 'var(--err)',
    accentSoft: 'var(--err-soft)',
    accentRgb: 'var(--err-rgb)',
    surface: 'rgba(var(--err-rgb), 0.1)',
    border: 'rgba(var(--err-rgb), 0.4)',
    onAccent: 'var(--on-err)',
    requiresReason: true,
    requiresTypeToConfirm: true,
    requiresConfirmation: true,
  },
};

export function dangerTierConfig(tier: DangerTier): DangerTierConfig {
  return DANGER_TIER_CONFIG[tier];
}

/**
 * A single downstream effect of an action, shown in the consequence panel.
 *
 * `severity` drives ordering and emphasis: the operator should read the worst
 * outcome first, not last.
 */
export interface Consequence {
  /** Plain language. "Week 7 is locked and scored" — not "weekState.status=locked". */
  text: string;
  severity: 'critical' | 'warning' | 'info';
  /** Optional count, e.g. 8 matchups affected. Rendered monospaced. */
  count?: number;
}

const SEVERITY_ORDER: Record<Consequence['severity'], number> = {
  critical: 0,
  warning: 1,
  info: 2,
};

export function sortConsequences(consequences: Consequence[]): Consequence[] {
  return [...consequences].sort(
    (a, b) => SEVERITY_ORDER[a.severity] - SEVERITY_ORDER[b.severity],
  );
}
